/* AA-INFINITE-SCROLL-V2 | Collections + Search */
(() => {
  'use strict';

  const MARKER = '[data-aa-infinite-pagination]';
  const SECTION = '.shopify-section--main-search, .shopify-section--main-collection';
  const states = new Map();
  const preloadDistance = 900;

  function nextURL(pagination) {
    return pagination.querySelector('a[rel="next"]')?.href || null;
  }

  function resolve(pagination) {
    const section = pagination.closest(SECTION);
    const grid = pagination.parentElement?.querySelector(
      'product-list.product-list'
    );
    return section && grid ? { section, grid } : null;
  }

  function scan() {
    for (const [pagination, state] of states) {
      if (
        !pagination.isConnected ||
        !state.grid.isConnected ||
        !state.section.contains(state.grid)
      ) {
        state.stop(false);
      }
    }

    document.querySelectorAll(MARKER).forEach(initialize);
  }

  function initialize(pagination) {
    if (states.has(pagination) || !pagination.isConnected) return;

    const scope = resolve(pagination);
    if (!scope || !nextURL(pagination)) return;

    const { section, grid } = scope;

    const trigger = document.createElement('div');
    trigger.className = 'aa-infinite-scroll-trigger';
    trigger.setAttribute('role', 'status');
    trigger.setAttribute('aria-live', 'polite');
    trigger.style.cssText =
      'min-height:2px;text-align:center;margin:1rem auto;';

    const state = {
      section,
      grid,
      pagination,
      trigger,
      busy: false,
      stopped: false,
      controller: null,
      raf: null,
      observer: null,
      stop: null
    };

    function stop(restore = false) {
      if (state.stopped) return;

      state.stopped = true;
      state.observer?.disconnect();
      state.controller?.abort();

      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);

      if (state.raf !== null) {
        cancelAnimationFrame(state.raf);
      }

      trigger.remove();

      if (restore && pagination.isConnected) {
        pagination.hidden = false;
      }

      states.delete(pagination);
    }

    state.stop = stop;

    async function load() {
      if (state.busy || state.stopped) return;

      if (
        !pagination.isConnected ||
        !grid.isConnected ||
        !section.contains(grid)
      ) {
        stop(false);
        return;
      }

      const url = nextURL(pagination);

      if (!url) {
        stop(false);
        return;
      }

      state.busy = true;
      state.controller = new AbortController();
      trigger.textContent = 'Loading more products…';

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          signal: state.controller.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const nextSection =
          doc.getElementById(section.id) ||
          doc.querySelector(SECTION);

        const nextPagination =
          nextSection?.querySelector(MARKER);

        const nextGrid =
          nextPagination?.parentElement?.querySelector(
            'product-list.product-list'
          );

        if (!nextPagination || !nextGrid) {
          throw new Error('Missing pagination or product grid');
        }

        if (
          !pagination.isConnected ||
          !grid.isConnected ||
          !section.contains(grid)
        ) {
          stop(false);
          return;
        }

        const additions = Array.from(nextGrid.children);
        const fragment = document.createDocumentFragment();

        // Dynamically inserted product cards must be visible immediately.
        // Vogue's initial scroll-reveal observer does not register them.
        additions.forEach(item => {
          if (item.matches('product-card[reveal-on-scroll]')) {
            item.removeAttribute('reveal-on-scroll');
            item.style.setProperty('opacity', '1', 'important');
            item.style.setProperty('transform', 'none', 'important');
          }

          fragment.appendChild(item);
        });

        grid.appendChild(fragment);

        pagination.innerHTML = nextPagination.innerHTML;
        trigger.textContent = '';

        if (!nextURL(pagination)) {
          stop(false);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[AA Infinite Scroll V2]', error);
          stop(true);
        }
      } finally {
        state.busy = false;

        if (!state.stopped) {
          schedule();
        }
      }
    }

    function check() {
      if (state.stopped || state.busy) return;

      const rect = trigger.getBoundingClientRect();

      // Includes fast scrolling beyond the trigger.
      if (rect.top <= window.innerHeight + preloadDistance) {
        void load();
      }
    }

    function schedule() {
      if (state.stopped || state.raf !== null) return;

      state.raf = requestAnimationFrame(() => {
        state.raf = null;
        check();
      });
    }

    pagination.before(trigger);
    states.set(pagination, state);
    pagination.hidden = true;

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            schedule();
          }
        },
        { rootMargin: `${preloadDistance}px 0px` }
      );

      state.observer.observe(trigger);
    }

    schedule();
  }

  function start() {
    scan();

    const observer = new MutationObserver(scan);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();
